import React, { useEffect, useState } from "react";
import { ButtonGroup, Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { fetchJobs, fetchReportJobs } from "../../services/httpClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import html2canvas from "html2canvas";
import { JobsTable } from "./charts-metrics/JobsTable";
import { signal } from "@preact/signals-react";
import { getCurrentDateTime } from "../../helpers/date";
import { CSVLink } from "react-csv";
import { JobsStackedBarChart } from "./charts-metrics/JobsStackedBarChart";

export const showJobs = signal(false);

const JobsTab = ({ buildPDF, canvas, setCanvas, iPad }: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reportRunning, setReportRunning] = useState<boolean>(false);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);

  const defaultCSVHeaders = [
    { label: "Department ID", key: "id", hide: true },
    { label: "Department", key: "department" },
    { label: "Job ID", key: "job_id", hide: true },
    { label: "Job", key: "job" },
    { label: "Case ID", key: "case_id" },
    { label: "Case", key: "case_name" },
    { label: "Name", key: "name" },
  ];

  useEffect(() => {
    getJobData();
  }, []);

  const getJobData = () => {
    fetchReportJobs()
      .then((res: any) => {
        const reportJobs = res[0].data;
        setAssignments(reportJobs);
        const deptCounts = reportJobs.reduce((acc: Record<string, number>, job: any) => {
          const dept = job.department;
          acc[dept] = (acc[dept] || 0) + 1;
          return acc;
        }, {});

        fetchJobs()
          .then((res: any) => {
            const jobs = res[0].data;
            showJobs.value = jobs.length > 0 ? true : false;

            const deptMap = new Map<string, any>();
            jobs.map((j: any) => {
              const deptID = j.department_id;
              const deptName = j.department;

              const count = deptCounts[deptName] ?? 0;
              if (!deptMap.has(deptName)) {
                deptMap.set(deptName, { id: deptID, name: `${deptName} (${count})`, key: deptName, count });
              }

              const open = j.positions_total - j.positions_filled;
              return {
                ...j,
                positions_open: open,
              };
            });
            setJobs(jobs);

            Object.entries(deptCounts).forEach(([dept, count]) => {
              const key = dept;
              if (!deptMap.has(key)) {
                deptMap.set(key, { name: `${key} (${count})`, key, count });
              } else {
                const existing = deptMap.get(key);
                existing.count = count;
                existing.name = `${key} (${count})`;
              }
            });

            const departments = Array.from(deptMap.values()).sort((a: any, b: any) => a.key.localeCompare(b.key));
            setDepartments(departments);
          })
          .catch((err: any) => {
            console.log(err);
          });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (reportRunning) {
      buildPDF();
      setReportRunning(false);
    }
  }, [canvas]);

  const onClickDownloadReport = async () => {
    setReportRunning(true);
    const reportSection = document.getElementById("jobs-section");
    const reportTitle = document.getElementById("jobs-title");
    const jobsFilters = document.getElementById("jobs-filters");
    const pagination = document.getElementById("jobs-pagination");

    if (reportSection) {
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }

      if (jobsFilters) {
        jobsFilters.style.visibility = "hidden";
        jobsFilters.style.display = "none";
      }

      if (pagination) {
        pagination.style.visibility = "hidden";
        pagination.style.display = "none";
      }

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }

      if (jobsFilters) {
        jobsFilters.style.visibility = "visible";
        jobsFilters.style.display = "flex";
      }

      if (pagination) {
        pagination.style.visibility = "visible";
        pagination.style.display = "flex";
      }
    } else {
      console.error("Report section not found");
      setReportRunning(false);
    }
  };

  return (
    <div id="jobs-section">
      {isLoading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </div>
      ) : (
        <>
          <Row id="jobs-title" className="mb-3">
            <Col className="col-8">
              <h4 className="mb-0 app-header-text">Job Assignments</h4>
            </Col>
            <Col className="col-4 text-end">
              {!iPad && (
                <Dropdown as={ButtonGroup}>
                  <Dropdown.Toggle id="dropdown-custom-1" className="app-primary-color button">
                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                    Downloads
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="super-colors">
                    <Dropdown.Item eventKey="1" onClick={() => onClickDownloadReport()}>
                      Report
                    </Dropdown.Item>
                    <Dropdown.Item
                      eventKey="2"
                      as={CSVLink}
                      data={assignments ? assignments : [{}]}
                      headers={defaultCSVHeaders}
                      filename={`job_assignments_${getCurrentDateTime()}.csv`}
                      target="_blank"
                    >
                      Data Export
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Col>
          </Row>
          <JobsStackedBarChart departments={departments} jobs={jobs} />
          <JobsTable data={assignments} deptList={departments} />
        </>
      )}
    </div>
  );
};

export default JobsTab;
